import AppLayout from '@components/layouts/appLayout';
import styled from '@emotion/styled';
import { Button } from '@lib/uikit';
import { AdData, VastPlugin } from '@lib/videojs/plugins/vast';
import { useCallback, useMemo, useRef, useState } from 'react';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';

import { StoryFooter } from '../components/layout/StoryFooter';
import { StoryHeader } from '../components/layout/StoryHeader';
import { StoryWrapper } from '../components/layout/StoryWrapper';
import VideoPlayer from '../components/video/VideoPlayer';
import { VideoNavigationOverlay } from '../components/VideoNavigationOverlay';
import { useNavigationContext } from '../contexts/NavigationContext';
import { usePageContext } from '../contexts/PageContext';
import { useProgressDispatchContext } from '../contexts/ProgressContext';
import { useStoryContext } from '../contexts/StoryContext';
import { VideoPlayerProvider } from '../contexts/VideoPlayerContext';
import { StoryPageDisplay, StoryPageType } from '../enums';
import { Renderer as RendererType, Tester } from '../types';

const Center = styled.div`
    display: flex;
    justify-content: center;
`;

interface VastCtaProps {
    onClick: () => void;
    text?: string;
}

const VastCta: React.FC<VastCtaProps> = ({ onClick, text = 'Learn More' }) => {
    return (
        <Center>
            <Button colorScheme='white' variant='solid' onClick={onClick}>
                {text}
            </Button>
        </Center>
    );
};

videojs.registerPlugin('vast', VastPlugin);
export const Renderer: RendererType = ({ story, storyPage }) => {
    const { ads } = storyPage;
    const dispatch = useProgressDispatchContext();
    const { next } = useNavigationContext();
    const { removePage } = useStoryContext();
    const { pageIndex } = usePageContext();
    const playerRef = useRef<Player>();
    const [adData, setAdData] = useState<AdData | null>(null);

    const popPage = useCallback(() => {
        if (pageIndex !== null) {
            removePage(pageIndex);
        }
    }, [removePage, pageIndex]);

    const handleOnReady = useCallback(
        (player: Player) => {
            playerRef.current = player;
            // @ts-expect-error - TODO: Poor videojs typing
            player.vast({
                url: ads?.vastUrl,
                debug: ads?.debug,
                timeout: ads?.timeout,
                onAdLoaded: (ad: AdData) => {
                    setAdData(ad);
                },
                onAdStart: (ad: AdData) => {
                    dispatch({
                        type: 'START',
                        payload: { duration: ad.duration },
                    });
                },
                onAdEnd: () => {
                    next();
                },
                onAdError: popPage,
            });
        },
        [ads, dispatch, next, popPage]
    );

    const handleOnDispose = useCallback((player: Player) => {
        // @ts-expect-error - TODO: Poor videojs typing
        player.vast().dispose();
    }, []);

    const handleAdClick = useCallback(() => {
        if (playerRef.current) {
            // @ts-expect-error - TODO: Poor videojs typing
            playerRef.current.vast().clickHandler();
        }
    }, []);

    // Create custom story page with ad information
    const storyPageWithAdInfo = {
        ...storyPage,
        title: adData?.adTitle || storyPage.title,
        subtitle: adData?.advertiser || storyPage.subtitle,
    };

    // Create custom CTA for VAST ad
    const customCta = useMemo(
        () =>
            adData?.clickThroughUrl ? (
                <VastCta onClick={handleAdClick} text={ads?.vastCtaText} />
            ) : null,
        [adData?.clickThroughUrl, ads?.vastCtaText, handleAdClick]
    );

    return (
        <VideoPlayerProvider storyPage={storyPage}>
            <AppLayout
                footerHasPointerEvents={false}
                headerPosition='absolute'
                footerPosition='absolute'
                header={
                    <StoryHeader
                        withExit
                        beforeExit={() => {
                            if (playerRef.current) {
                                // @ts-expect-error - TODO: Poor videojs typing
                                playerRef.current.vast().exitHandler();
                            }
                        }}
                        withPreviewImage={false}
                        story={story}
                        storyPage={storyPageWithAdInfo}
                    />
                }
                body={
                    <StoryWrapper>
                        <VideoNavigationOverlay />
                        <VideoPlayer
                            onReady={handleOnReady}
                            onDispose={handleOnDispose}
                            style={{
                                objectFit: 'contain',
                            }}
                        />
                    </StoryWrapper>
                }
                footer={
                    <StoryFooter
                        storyPage={storyPageWithAdInfo}
                        withCtas={false}
                        withNavigationButtons={false}
                        withVideoPlayerButtons={true}
                        customCtas={customCta}
                    />
                }
            />
        </VideoPlayerProvider>
    );
};

export const tester: Tester = (display, storyPage) => {
    return {
        condition:
            display === StoryPageDisplay.Default &&
            storyPage.type === StoryPageType.Ads,
        priority: 1,
    };
};

const rendererWithTester = { renderer: Renderer, tester };

export default rendererWithTester;
